import { useRef, type ReactNode } from 'react';
import { clampSideWidth, SIDE_DEFAULT, SIDE_MAX, SIDE_MIN, storeSideWidth } from './side-width.js';

/**
 * 侧栏和内容区之间那条可拖的分隔线。
 *
 * 三处刻意的做法：
 *
 * - **拖动期间直接改 CSS 变量，不走 React state。** 每次 pointermove 都 setState
 *   会把整棵树和右边的详情一起重渲染，拖起来发涩。松手时才提交一次。
 * - **用 pointer 事件加 setPointerCapture**，不是 mousemove/mouseup：手指和触控笔一样能拖，
 *   而且指针移出这条线之外（甚至移出窗口）也还跟着，不会拖一半就断。
 * - **键盘能调**：role="separator" 加左右方向键。分隔条是最常被漏掉键盘支持的控件之一，
 *   而它恰好是纯键盘用户唯一能改布局的地方。
 *
 * 双击回到默认宽度——拖坏了不用来回试。
 */
export function SideResizer({
  shellRef,
  width,
  onCommit,
}: {
  /** 宽度是写在这个元素上的 CSS 变量，拖动时直接改它。 */
  shellRef: React.RefObject<HTMLDivElement | null>;
  width: number;
  /** 松手（或按键）之后的最终宽度，交给外层落到 state 和 localStorage。 */
  onCommit: (px: number) => void;
}): ReactNode {
  const bar = useRef<HTMLDivElement | null>(null);

  function apply(px: number): number {
    const next = clampSideWidth(px, window.innerWidth);
    shellRef.current?.style.setProperty('--side-w', `${next}px`);
    return next;
  }

  function onPointerDown(event: React.PointerEvent<HTMLDivElement>): void {
    // 右键和中键不该开始拖
    if (event.button !== 0) return;

    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    bar.current?.classList.add('on');
    // 拖的时候别选中文字
    document.body.classList.add('resizing');

    const startX = event.clientX;
    const startWidth = width;
    let latest = startWidth;

    const move = (moveEvent: PointerEvent): void => {
      latest = apply(startWidth + (moveEvent.clientX - startX));
    };

    const end = (): void => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      bar.current?.classList.remove('on');
      document.body.classList.remove('resizing');
      onCommit(latest);
      storeSideWidth(latest);
    };

    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    // 按住 shift 走大步，跟大多数分隔条一致
    const step = event.shiftKey ? 48 : 16;
    const target =
      event.key === 'ArrowLeft'
        ? width - step
        : event.key === 'ArrowRight'
          ? width + step
          : event.key === 'Home'
            ? SIDE_DEFAULT
            : null;
    if (target === null) return;

    event.preventDefault();
    const next = apply(target);
    onCommit(next);
    storeSideWidth(next);
  }

  return (
    <div
      ref={bar}
      className="side-resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label="调整侧边栏宽度"
      aria-valuenow={width}
      aria-valuemin={SIDE_MIN}
      aria-valuemax={SIDE_MAX}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={() => {
        const next = apply(SIDE_DEFAULT);
        onCommit(next);
        storeSideWidth(next);
      }}
    />
  );
}
